import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  dateRangeAtom,
  limitAtom,
  queryAtom,
  resetFiltersAtom,
  sportsAtom,
} from "@/state/filters.state";
import { sportLabel } from "@/types/sports.meta";
import { plural, t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { ChevronDown, Filter } from "lucide-react";
import { useMemo, useState } from "react";
import { DatePicker } from "./DatePicker";
import { SportsPicker } from "./SportsPicker";

type ActivityFiltersProps = {
  variant?: "sidebar" | "mobile";
  showTitle?: boolean; // default true for sidebar
};

export function ActivityFilters({
  variant = "sidebar",
  showTitle = true,
}: ActivityFiltersProps) {
  const [query, setQuery] = useAtom(queryAtom);
  const sports = useAtomValue(sportsAtom);
  const range = useAtomValue(dateRangeAtom);
  const [limit, setLimit] = useAtom(limitAtom);
  const resetFilters = useSetAtom(resetFiltersAtom);

  const [sportOpen, setSportOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);

  const { i18n } = useLingui();

  const isMobile = variant === "mobile";

  const df = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      }),
    [],
  );

  const rangeLabel =
    range.from && range.to
      ? t`${df.format(range.from)} → ${df.format(range.to)}`
      : range.from
        ? t`${df.format(range.from)} → …`
        : t`Pick a date range`;

  const sportsLabel = (() => {
    switch (sports.mode) {
      case "all":
        return t`All sports`;

      case "include": {
        const count = sports.sports.length;

        if (count === 0) {
          return t`No sports`;
        }
        if (count === 1) {
          return i18n._(sportLabel(sports.sports[0]));
        }
        return plural(count, {
          one: "# sport",
          other: "# sports",
        });
      }

      case "exclude": {
        const count = sports.sports.length;

        if (count === 0) {
          return t`All sports`;
        }
        return plural(count, {
          one: "All except # sport",
          other: "All except # sports",
        });
      }
    }
  })();

  const wrapperClass = variant === "sidebar" ? "px-3 py-4" : "p-3";

  // --- Render ----------------------------------------------------------------

  return (
    <div className={cn("min-w-0", wrapperClass)}>
      {/* Title */}
      {showTitle ? (
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold leading-none">{t`Filters`}</h2>
          </div>
        </div>
      ) : null}

      <div className="space-y-3">
        {/* Search */}
        <section className="rounded-lg border bg-card p-3 shadow-sm">
          <div className="mb-2 text-xs font-medium text-muted-foreground">
            {t`Search`}
          </div>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t`Search activity name…`}
            className="text-sm"
          />
        </section>

        {/* Sport */}
        <section className="rounded-lg border bg-card p-3 shadow-sm">
          <div className="mb-2 text-xs font-medium text-muted-foreground">
            {t`Sport`}
          </div>

          {isMobile ? (
            <>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-between font-normal"
                onClick={() => setSportOpen(true)}
              >
                <span className="truncate">{sportsLabel}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>

              <Dialog open={sportOpen} onOpenChange={setSportOpen}>
                <DialogContent className="h-[100dvh] w-[100vw] max-w-none rounded-none p-0">
                  <div className="flex h-full flex-col overflow-hidden">
                    <DialogHeader className="border-b p-4">
                      <div className="flex items-center justify-center gap-3">
                        <DialogTitle>{t`Sports`}</DialogTitle>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <DialogDescription>{t`Pick one or more sports`}</DialogDescription>
                      </div>
                    </DialogHeader>

                    <SportsPicker onDone={() => setSportOpen(false)} />
                  </div>
                </DialogContent>
              </Dialog>
            </>
          ) : (
            <Popover open={sportOpen} onOpenChange={setSportOpen} modal>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between font-normal"
                >
                  <span className="truncate">{sportsLabel}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>

              <PopoverContent
                align="start"
                side="right"
                sideOffset={10}
                collisionPadding={12}
                className={cn(
                  "w-[--radix-popover-trigger-width] min-w-64 p-2",
                  "flex flex-col max-h-[75vh] overflow-hidden",
                )}
              >
                <SportsPicker onDone={() => setSportOpen(false)} />
              </PopoverContent>
            </Popover>
          )}
        </section>

        {/* Date range */}
        <section className="rounded-lg border bg-card p-3 shadow-sm">
          <div className="mb-2 text-xs font-medium text-muted-foreground">
            {t`Date range`}
          </div>

          {isMobile ? (
            <>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-between font-normal"
                onClick={() => setDateOpen(true)}
              >
                <span className="truncate">{rangeLabel}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>

              <Dialog open={dateOpen} onOpenChange={setDateOpen}>
                <DialogContent className="h-[100dvh] w-[100vw] max-w-none rounded-none p-0">
                  <div className="flex h-full flex-col overflow-hidden">
                    <DialogHeader className="border-b p-4">
                      <div className="flex items-center justify-center gap-3">
                        <DialogTitle>{t`Date range`}</DialogTitle>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <DialogDescription>
                          {t`Pick a range or use a preset`}
                        </DialogDescription>
                      </div>
                    </DialogHeader>

                    <div className="min-h-0 flex flex-1 p-4">
                      <DatePicker
                        variant={variant}
                        onDone={() => setDateOpen(false)}
                      />
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          ) : (
            <Popover open={dateOpen} onOpenChange={setDateOpen} modal>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between font-normal"
                >
                  <span className="truncate">{rangeLabel}</span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>

              <PopoverContent
                align="start"
                side="right"
                sideOffset={10}
                collisionPadding={12}
                className={cn("p-2", "w-auto", "max-h-[85vh] overflow-auto")}
              >
                <DatePicker
                  variant={variant}
                  onDone={() => setDateOpen(false)}
                />
              </PopoverContent>
            </Popover>
          )}
        </section>

        {/* Limit */}
        <section className="rounded-lg border bg-card p-3 shadow-sm">
          <div className="mb-2 text-xs font-medium text-muted-foreground">
            {t`Max activities`}
          </div>

          <Select
            value={limit === undefined ? "1000" : String(limit)}
            onValueChange={(v) => setLimit(Number(v))}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="1000" />
            </SelectTrigger>

            <SelectContent
              align="start"
              side="bottom"
              sideOffset={8}
              collisionPadding={12}
              className="w-[min(var(--radix-select-trigger-width),calc(100vw-1.5rem))] max-h-[60vh]"
            >
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="1000">1,000</SelectItem>
              <SelectItem value="10000">10,000</SelectItem>
            </SelectContent>
          </Select>
        </section>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={resetFilters} className="w-full">
            {t`Reset filters`}
          </Button>
        </div>
      </div>
    </div>
  );
}
